import ACLProfile from '@app/api/aclprofile/aclprofile.actions';

/**
 * Consolidated ACL data fetching utility
 * Reduces multiple API calls to single optimized calls
 */

export interface ConsolidatedACLData {
  allProfiles: any[];
  siteProfiles: any[];
  wanProfiles: any[];
  combinedProfiles: any[];
}

/**
 * Fetch all ACL data needed for site view page in ONE SINGLE optimized call
 */
export const fetchSiteViewACLData = async (siteId: string, wanId?: string): Promise<ConsolidatedACLData> => {
  try {
    // Single call to get all profiles, then filter client-side
    const allProfiles = await ACLProfile.getAll() || [];

    // Filter assigned profiles client-side (same logic as getAssignedACLs)
    const siteProfiles = allProfiles.filter(acl => {
      if (acl.assignments) {
        return acl.assignments.some(assignment => assignment.resource_id === siteId);
      }
      return false;
    });

    const wanProfiles = wanId ? allProfiles.filter(acl => {
      if (acl.assignments) {
        return acl.assignments.some(assignment => assignment.resource_id === wanId);
      }
      return false;
    }) : [];

    const combinedProfiles = siteProfiles.concat(wanProfiles);

    return {
      allProfiles,
      siteProfiles,
      wanProfiles,
      combinedProfiles
    };
  } catch (error) {
    console.error(' ACLUtils: Failed to fetch site view ACL data:', error);
    throw error;
  }
};

/**
 * Fetch all ACL data needed for site edit page in ONE SINGLE optimized call
 * Replaces 3 separate API calls with 1 single call 
 */
export const fetchSiteEditACLData = async (siteId: string, wanId?: string): Promise<ConsolidatedACLData> => {
  try {
    //  Single call to get all profiles, then filter client-side
    const allProfiles = await ACLProfile.getAll() || [];

    // Filter assigned profiles client-side (same logic as getAssignedACLs)
    const siteProfiles = allProfiles.filter(acl => {
      if (acl.assignments) {
        return acl.assignments.some(assignment => assignment.resource_id === siteId);
      }
      return false;
    });

    const wanProfiles = wanId ? allProfiles.filter(acl => {
      if (acl.assignments) {
        return acl.assignments.some(assignment => assignment.resource_id === wanId);
      }
      return false;
    }) : [];

    const combinedProfiles = siteProfiles.concat(wanProfiles);

    return {
      allProfiles,
      siteProfiles,
      wanProfiles,
      combinedProfiles
    };
  } catch (error) {
    console.error(' ACLUtils: Failed to fetch site edit ACL data:', error);
    throw error;
  }
};

/**
 * Fetch ACL data for WAN selection change (used in edit page)
 * Uses single call approach for consistency
 */
export const fetchWanSelectionACLData = async (siteId: string, wanId: string): Promise<ConsolidatedACLData> => {
  try {
    // Single call to get all profiles, then filter client-side
    const allProfiles = await ACLProfile.getAll() || [];

    // Filter assigned profiles client-side
    const siteProfiles = allProfiles.filter(acl => {
      if (acl.assignments) {
        return acl.assignments.some(assignment => assignment.resource_id === siteId);
      }
      return false;
    });

    const wanProfiles = allProfiles.filter(acl => {
      if (acl.assignments) {
        return acl.assignments.some(assignment => assignment.resource_id === wanId);
      }
      return false;
    });

    const combinedProfiles = siteProfiles.concat(wanProfiles);

    return {
      allProfiles,
      siteProfiles,
      wanProfiles,
      combinedProfiles
    };
  } catch (error) {
    console.error(' ACLUtils: Failed to fetch WAN selection ACL data:', error);
    throw error;
  }
};
